import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { UploadSimple, ArrowsClockwise, FloppyDiskBack, XCircle, DownloadSimple } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import api from '../../../utils/api';

const FIELD_OPTIONS = [
  { value: 'name', label: 'Name', required: true, aliases: ['full name', 'contact name'] },
  { value: 'email', label: 'Email', required: true, aliases: ['email address', 'e-mail'] },
  { value: 'phone', label: 'Phone', aliases: ['mobile', 'telephone'] },
  { value: 'leadSource', label: 'Source', aliases: ['source', 'lead source'] },
  { value: 'priority', label: 'Priority', aliases: ['hot', 'warm', 'cold'] },
  { value: 'weddingDate', label: 'Wedding Date', aliases: ['weddingdate', 'event date', 'date'] },
  { value: 'eventDate', label: 'Event Date', aliases: ['eventdate'] },
  { value: 'stage', label: 'Stage', aliases: ['pipeline stage'] },
  { value: 'tags', label: 'Tags (comma separated)', aliases: [] },
  { value: 'notes', label: 'Notes', aliases: ['comments'] },
  { value: 'custom:', label: 'Custom Field (create from header)', aliases: [] }
];

const STEPS = ['Upload', 'Map Fields', 'Preview', 'Review'];

const MAX_ROWS = 2000;

function normalizeKey(val = '') {
  return val.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function similarity(a = '', b = '') {
  const normA = normalizeKey(a);
  const normB = normalizeKey(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1;
  if (normA.includes(normB) || normB.includes(normA)) return 0.8;
  return 0;
}

function autoMapHeaders(headers = []) {
  return headers.map((header) => {
    const best = FIELD_OPTIONS.reduce(
      (bestMatch, option) => {
        const score = Math.max(
          similarity(header, option.label),
          ...(option.aliases || []).map(alias => similarity(header, alias))
        );
        if (score > bestMatch.score) {
          return { score, option };
        }
        return bestMatch;
      },
      { score: 0, option: null }
    );
    const target = best.score >= 0.6 && best.option ? best.option.value : '';
    return { source: header, target, confidence: best.score };
  });
}

function parseFile(file) {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      try {
        if (ext === 'json') {
          const json = JSON.parse(reader.result);
          const rows = Array.isArray(json) ? json : [];
          return resolve(rows.slice(0, MAX_ROWS));
        }
        const data = reader.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        resolve(rows.slice(0, MAX_ROWS));
      } catch (err) {
        reject(err);
      }
    };
    if (ext === 'json') {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
}

const CRMImportModal = ({ open, onClose, businessId, onImported }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [fileMeta, setFileMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [decisions, setDecisions] = useState({});
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && businessId) {
      fetchTemplates();
      fetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, businessId]);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/api/leads/import/templates', { params: { businessId } });
      setTemplates(res.data.templates || []);
    } catch (err) {}
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setLoading(true);
    try {
      const parsed = await parseFile(file);
      const cols = parsed.length ? Object.keys(parsed[0]) : [];
      setRows(parsed);
      setHeaders(cols);
      setMapping(autoMapHeaders(cols));
      setFileMeta({ name: file.name, size: file.size, rows: parsed.length });
      setActiveStep(1);
    } catch (err) {
      setError('Unable to parse file. Please ensure it is valid CSV/XLSX/JSON.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/api/leads/import/history', { params: { businessId } });
      setHistory(res.data.sessions || []);
    } catch (err) {}
  };

  const handleTemplateApply = (templateId) => {
    const tpl = templates.find(t => t._id === templateId);
    setSelectedTemplate(templateId);
    if (tpl?.mapping && headers.length) {
      const mappingArr = headers.map(h => {
        const target = tpl.mapping.find(m => m.source === h)?.target || '';
        return { source: h, target, confidence: 1 };
      });
      setMapping(mappingArr);
    }
  };

  const handleTemplateSave = async () => {
    if (!templateName) {
      setError('Template name is required');
      return;
    }
    try {
      setError('');
      await api.post('/api/leads/import/templates', {
        businessId,
        name: templateName,
        mapping
      });
      setTemplateName('');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    }
  };

  const handleResetMapping = () => {
    setMapping(autoMapHeaders(headers));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(mapping);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setMapping(reordered);
  };

  const handleMappingChange = (index, target) => {
    const updated = [...mapping];
    updated[index] = { ...updated[index], target };
    setMapping(updated);
  };

  const normalizedMapping = useMemo(() => mapping.filter(m => m.target), [mapping]);

  const proceedPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/leads/import/preview', {
        businessId,
        mapping: normalizedMapping,
        rows,
        dedupeBy: ['email', 'phone']
      });
      setPreview(res.data.preview || []);
      setSessionId(res.data.sessionId);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to build preview');
    } finally {
      setLoading(false);
    }
  };

  const setDecision = (index, update) => {
    setDecisions(prev => ({ ...prev, [index]: { ...(prev[index] || {}), ...update } }));
  };

  const commitImport = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const decisionArray = Object.entries(decisions).map(([index, value]) => ({
        index: Number(index),
        ...value
      }));
      const res = await api.post('/api/leads/import/commit', {
        sessionId,
        decisions: decisionArray
      });
      setSummary(res.data.summary);
      setActiveStep(3);
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const rollbackImport = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/api/leads/import/rollback', { sessionId });
      setSummary({ ...summary, rolledBack: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Rollback failed');
    } finally {
      setLoading(false);
    }
  };

  const restoreImport = async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/api/leads/import/restore', { sessionId: id });
      await fetchHistory();
      if (id === sessionId) {
        setSummary(summary ? { ...summary, restored: true } : summary);
      }
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Restore failed');
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setActiveStep(0);
    setFileMeta(null);
    setRows([]);
    setHeaders([]);
    setMapping([]);
    setPreview([]);
    setSessionId('');
    setDecisions({});
    setSummary(null);
    setError('');
  };

  const closeModal = () => {
    resetAll();
    onClose?.();
  };

  const renderUpload = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Upload CSV, XLSX, or JSON. We auto-detect headers and suggest mappings. Max {MAX_ROWS} rows per upload.
      </Typography>
      <Button
        variant="text"
        startIcon={<DownloadSimple size={18} />}
        onClick={() => {
          const csvHeaders = [
            'name',
            'email',
            'phone',
            'leadSource',
            'priority',
            'stage',
            'weddingDate',
            'eventDate',
            'tags',
            'notes'
          ];
          const sample = [
            csvHeaders.join(','),
            'Alex Smith,alex@example.com,5551231111,Website,hot,enquired,2025-06-12,2025-05-30,"venue,priority",Looking for venue options',
            'Jamie Lee,jamie@example.com,5559872222,Referral,warm,in_progress,2025-09-20,2025-09-10,"catering,followup",Asked about catering'
          ].join('\n');
          const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'crm-import-template.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }}
      >
        Download CSV template
      </Button>
      <Button
        component="label"
        variant="outlined"
        startIcon={<UploadSimple size={18} />}
      >
        Choose File
        <input type="file" accept=".csv,.xlsx,.json" hidden onChange={handleFileChange} />
      </Button>
      {fileMeta && (
        <Alert severity="info">
          {fileMeta.name} • {fileMeta.rows} rows
        </Alert>
      )}
    </Stack>
  );

  const renderMapping = () => (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Templates</InputLabel>
          <Select
            value={selectedTemplate}
            onChange={(e) => handleTemplateApply(e.target.value)}
            label="Templates"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {templates.map(t => (
              <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Save as template"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <Button variant="outlined" onClick={handleTemplateSave} startIcon={<FloppyDiskBack size={16} />}>
          Save
        </Button>
        <Button variant="text" onClick={handleResetMapping} startIcon={<ArrowsClockwise size={16} />}>
          Reset auto-map
        </Button>
      </Stack>
      <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="mapping">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {mapping.map((item, idx) => (
                  <Draggable draggableId={item.source} index={idx} key={item.source}>
                    {(drag) => (
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        {...drag.dragHandleProps}
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <Typography sx={{ width: 200 }} variant="body2">{item.source}</Typography>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Map to</InputLabel>
                          <Select
                            value={item.target || ''}
                            label="Map to"
                            onChange={(e) => handleMappingChange(idx, e.target.value)}
                          >
                            <MenuItem value=""><em>Skip</em></MenuItem>
                            {FIELD_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {item.confidence >= 0.8 && (
                          <Chip size="small" color="success" label="Auto" />
                        )}
                        {item.confidence > 0 && item.confidence < 0.8 && (
                          <Chip size="small" label="Check" />
                        )}
                      </Stack>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Stack>
  );

  const renderPreview = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Auto-detected duplicates by email/phone. Choose action per record (import, merge, skip).
      </Alert>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Import Record</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Stage</TableCell>
            <TableCell>Suggested</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Existing Matches</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preview.map((row) => {
            const decision = decisions[row.index]?.action || row.suggestedAction;
            const uniqueMatches = Array.from(
              new Map((row.matches || []).map((m) => [m._id, m])).values()
            );
            return (
              <TableRow key={row.index}>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {row.data.name || 'New record'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Import
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{row.data.email}</Typography>
                    <Typography variant="body2">{row.data.phone}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{row.data.stage}</TableCell>
                <TableCell>{row.suggestedAction}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={decision || 'import'}
                    onChange={(e) => setDecision(row.index, { action: e.target.value })}
                  >
                    <MenuItem value="import">Import</MenuItem>
                    <MenuItem value="merge" disabled={!row.matches?.length}>Merge</MenuItem>
                    <MenuItem value="skip">Skip</MenuItem>
                  </Select>
                  {decision === 'merge' && row.matches?.length > 0 && (
                    <Select
                      size="small"
                      sx={{ mt: 1, minWidth: 180 }}
                      value={decisions[row.index]?.mergeLeadId || ''}
                      onChange={(e) => setDecision(row.index, { action: 'merge', mergeLeadId: e.target.value })}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select existing to merge</em>
                      </MenuItem>
                      {uniqueMatches.map(m => {
                        const contact = m.email || m.phone || 'no contact';
                        const suffix = m._id ? ` • ${m._id.slice(-6)}` : '';
                        return (
                          <MenuItem key={m._id} value={m._id}>
                            Existing: {m.name} ({contact}){suffix}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {uniqueMatches.length ? (
                    <Stack spacing={0.5}>
                      {uniqueMatches.map(m => (
                        <Chip
                          key={m._id}
                          size="small"
                          label={`Existing: ${m.name} (${m.email || m.phone || 'no contact'})${m._id ? ` • ${m._id.slice(-6)}` : ''}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">None</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );

  const renderSummary = () => (
    <Stack spacing={2}>
      <Typography variant="body1">Import complete.</Typography>
      {summary && (
        <Stack direction="row" spacing={2}>
          <Chip label={`Created ${summary.created || 0}`} color="primary" />
          <Chip label={`Updated ${summary.updated || 0}`} color="success" />
          <Chip label={`Skipped ${summary.skipped || 0}`} />
          <Chip label={`Errors ${summary.errors || 0}`} color="error" />
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="outlined" onClick={rollbackImport} startIcon={<ArrowsClockwise size={16} />}>
          Rollback
        </Button>
        <Button variant="contained" onClick={closeModal}>
          Close
        </Button>
      </Stack>
      <Divider />
      <Typography variant="subtitle2" color="text.secondary">
        Restore points
      </Typography>
      <Stack spacing={1}>
        {history.map((h) => (
          <Box key={h._id} display="flex" justifyContent="space-between" alignItems="center">
            <Stack spacing={0}>
              <Typography variant="body2">Imported: {new Date(h.createdAt).toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">
                Status: {h.status}{h.restoredAt ? ` • Restored ${new Date(h.restoredAt).toLocaleString()}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {h.results?.summary ? `C:${h.results.summary.created} U:${h.results.summary.updated} S:${h.results.summary.skipped} E:${h.results.summary.errors}` : ''}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              disabled={h.status !== 'committed'}
              onClick={() => restoreImport(h._id)}
            >
              Restore
            </Button>
          </Box>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Import couples</Typography>
        <IconButton onClick={closeModal}>
          <XCircle size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {activeStep === 0 && renderUpload()}
        {activeStep === 1 && renderMapping()}
        {activeStep === 2 && renderPreview()}
        {activeStep === 3 && renderSummary()}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={() => setActiveStep(activeStep - 1)} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep === 0 && (
          <Button onClick={closeModal}>Cancel</Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={proceedPreview}
            disabled={loading || !rows.length}
          >
            Preview
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={commitImport}
            disabled={loading || !sessionId}
          >
            Import
          </Button>
        )}
        {activeStep === 3 && (
          <Button onClick={closeModal}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CRMImportModal;
